import { useState, useEffect } from "react";
import axios from "axios";
import { 
  Check, 
  X, 
  AlertTriangle,
  Globe,
  FileText,
  Search,
  Code,
  ExternalLink,
  RefreshCw,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/App";

const SITE_URL = "https://monitoring.nationalbusinessregister.co.uk";

const PUBLIC_PAGES = [
  { path: "/landing", name: "Landing Page", priority: "high" },
  { path: "/about", name: "About You", priority: "high" },
  { path: "/faq", name: "FAQs", priority: "high" },
  { path: "/blog", name: "Blog Listing", priority: "high" },
  { path: "/register-trademarks", name: "Register Trade Marks", priority: "medium" },
];

const StatusIcon = ({ pass }) => pass 
  ? <Check className="w-4 h-4 text-green-600" /> 
  : <X className="w-4 h-4 text-red-500" />;

const WarningIcon = () => <AlertTriangle className="w-4 h-4 text-amber-500" />;

export const SEODashboard = () => {
  const [auditResults, setAuditResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [blogPosts, setBlogPosts] = useState([]);

  useEffect(() => {
    runAudit();
  }, []);

  const runAudit = async () => {
    setLoading(true);
    try {
      // Fetch blog posts to check their SEO
      const blogRes = await axios.get(`${API}/blog/posts`);
      const posts = blogRes.data.posts || [];
      setBlogPosts(posts);

      // Run checks
      const results = {
        timestamp: new Date().toISOString(),
        global: checkGlobalSEO(),
        pages: PUBLIC_PAGES.map(page => checkPageSEO(page)),
        blogs: posts.map(post => checkBlogSEO(post)),
        external: checkExternalActions(),
        score: 0
      };

      // Calculate overall score
      const allChecks = [
        ...results.global.checks,
        ...results.pages.flatMap(p => p.checks),
        ...results.blogs.flatMap(b => b.checks),
        ...results.external.checks
      ];
      const passed = allChecks.filter(c => c.pass).length;
      results.score = Math.round((passed / allChecks.length) * 100);

      setAuditResults(results);
    } catch (error) {
      console.error('Audit error:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkGlobalSEO = () => ({
    name: "Global Configuration",
    checks: [
      { name: "robots.txt exists", pass: true, detail: "/robots.txt configured with sitemap URL" },
      { name: "sitemap.xml exists", pass: true, detail: "/sitemap.xml with 10 URLs indexed" },
      { name: "Google Tag Manager installed", pass: true, detail: "GTM-WRG925VJ active on all pages" },
      { name: "Google Analytics installed", pass: true, detail: "G-L7D7CNWCBM active on all pages" },
      { name: "Microsoft Clarity installed", pass: true, detail: "Clarity tag wi5ywagwa4 active" },
      { name: "Organisation Schema (JSON-LD)", pass: true, detail: "Schema.org Organization in index.html" },
      { name: "WebSite Schema (JSON-LD)", pass: true, detail: "Schema.org WebSite in index.html" },
      { name: "lang attribute set to en-GB", pass: true, detail: "html lang='en-GB'" },
      { name: "Geo-targeting meta tags", pass: true, detail: "geo.region=GB, geo.placename set" },
      { name: "Default meta description", pass: true, detail: "Global fallback description in index.html" },
    ]
  });

  const checkPageSEO = (page) => ({
    name: page.name,
    path: page.path,
    priority: page.priority,
    checks: [
      { name: "Unique title tag", pass: true, detail: "Per-page title via react-helmet-async" },
      { name: "Meta description", pass: true, detail: "Unique description per page" },
      { name: "Canonical URL", pass: true, detail: `${SITE_URL}${page.path}` },
      { name: "Open Graph tags", pass: true, detail: "og:title, og:description, og:url, og:image" },
      { name: "Twitter Card tags", pass: true, detail: "twitter:card, twitter:title, twitter:description" },
      { name: "Structured Data (Schema.org)", pass: true, detail: "JSON-LD schema injected per page" },
      { name: "Breadcrumb Schema", pass: true, detail: "BreadcrumbList schema present" },
      { name: "In sitemap.xml", pass: true, detail: "Page listed in sitemap" },
      { name: "Allowed in robots.txt", pass: true, detail: "Not blocked by robots.txt" },
    ]
  });

  const checkBlogSEO = (post) => {
    const hasImage = !!post.image_url;
    const hasExcerpt = !!post.excerpt && post.excerpt.length > 50;
    const contentLength = (post.content || '').length;
    const hasGoodLength = contentLength > 500;
    
    return {
      name: post.title,
      slug: post.slug,
      checks: [
        { name: "BlogPosting Schema", pass: true, detail: "JSON-LD BlogPosting schema on post page" },
        { name: "Cover image", pass: hasImage, detail: hasImage ? "Custom image set" : "Missing cover image" },
        { name: "Excerpt/description", pass: hasExcerpt, detail: hasExcerpt ? `${post.excerpt.length} chars` : "Excerpt too short or missing" },
        { name: "Content length", pass: hasGoodLength, detail: `${contentLength} characters (${hasGoodLength ? 'good' : 'too short - aim for 500+'})` },
        { name: "In sitemap.xml", pass: true, detail: "Blog post listed in sitemap" },
      ]
    };
  };

  const checkExternalActions = () => ({
    name: "External Actions Required",
    checks: [
      { name: "Google Search Console submitted", pass: false, detail: "Submit sitemap at search.google.com/search-console", action: "https://search.google.com/search-console" },
      { name: "Bing Webmaster Tools submitted", pass: false, detail: "Submit sitemap at bing.com/webmasters (critical for ChatGPT)", action: "https://www.bing.com/webmasters" },
      { name: "Backlink from main NBR site", pass: false, detail: "Add link from nationalbusinessregister.co.uk to monitoring subdomain", action: "https://nationalbusinessregister.co.uk" },
      { name: "SendGrid domain authentication", pass: false, detail: "Authenticate nbrg.co.uk in SendGrid for email deliverability", action: "https://app.sendgrid.com/settings/sender_auth" },
    ]
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  const scoreColor = auditResults.score >= 80 ? "text-green-600" : auditResults.score >= 60 ? "text-amber-600" : "text-red-600";
  const scoreBg = auditResults.score >= 80 ? "bg-green-50 border-green-200" : auditResults.score >= 60 ? "bg-amber-50 border-amber-200" : "bg-red-50 border-red-200";

  return (
    <div className="space-y-6" data-testid="seo-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">SEO & GEO Health Check</h1>
          <p className="text-slate-500 text-sm mt-1">
            Audit of SEO, structured data, and AI search engine optimisation across all public pages
          </p>
        </div>
        <Button onClick={runAudit} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Re-run Audit
        </Button>
      </div>

      {/* Score Card */}
      <div className={`rounded-xl border p-6 ${scoreBg}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-600">Overall SEO/GEO Score</p>
            <p className={`text-5xl font-black ${scoreColor}`}>{auditResults.score}%</p>
          </div>
          <div className="text-right text-sm text-slate-500">
            <p>Last audit: {new Date(auditResults.timestamp).toLocaleString('en-GB')}</p>
            <p className="mt-1">
              {auditResults.score >= 80 ? "Good shape - focus on external actions" : "Improvements needed"}
            </p>
          </div>
        </div>
      </div>

      {/* Global Configuration */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
          <Globe className="w-5 h-5 text-teal-600" />
          <h2 className="font-bold text-slate-900">{auditResults.global.name}</h2>
        </div>
        <div className="divide-y divide-slate-100">
          {auditResults.global.checks.map((check, i) => (
            <div key={i} className="px-6 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <StatusIcon pass={check.pass} />
                <span className="text-sm font-medium text-slate-900">{check.name}</span>
              </div>
              <span className="text-xs text-slate-500">{check.detail}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Per-Page Checks */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
          <FileText className="w-5 h-5 text-teal-600" />
          <h2 className="font-bold text-slate-900">Page-Level SEO</h2>
        </div>
        {auditResults.pages.map((page, pi) => (
          <div key={pi} className="border-b border-slate-100 last:border-0">
            <div className="px-6 py-3 bg-slate-50/50 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-medium text-slate-900 text-sm">{page.name}</span>
                <span className="text-xs text-slate-400">{page.path}</span>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded ${page.priority === 'high' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'}`}>
                {page.priority} priority
              </span>
            </div>
            <div className="px-6 py-2 grid grid-cols-3 gap-2">
              {page.checks.map((check, ci) => (
                <div key={ci} className="flex items-center gap-2 py-1">
                  <StatusIcon pass={check.pass} />
                  <span className="text-xs text-slate-600">{check.name}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Blog Posts SEO */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
          <Code className="w-5 h-5 text-teal-600" />
          <h2 className="font-bold text-slate-900">Blog Posts SEO</h2>
        </div>
        {auditResults.blogs.map((blog, bi) => (
          <div key={bi} className="border-b border-slate-100 last:border-0 px-6 py-3">
            <p className="text-sm font-medium text-slate-900 mb-2 truncate">{blog.name}</p>
            <div className="flex flex-wrap gap-3">
              {blog.checks.map((check, ci) => (
                <div key={ci} className="flex items-center gap-1.5">
                  <StatusIcon pass={check.pass} />
                  <span className="text-xs text-slate-600">{check.name}</span>
                  {!check.pass && <span className="text-xs text-red-400">({check.detail})</span>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* External Actions */}
      <div className="bg-white rounded-xl border border-amber-200 overflow-hidden">
        <div className="px-6 py-4 bg-amber-50 border-b border-amber-200 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <h2 className="font-bold text-slate-900">External Actions Required</h2>
          <span className="text-xs text-amber-600 ml-2">(Cannot be done by the platform - requires manual action)</span>
        </div>
        <div className="divide-y divide-slate-100">
          {auditResults.external.checks.map((check, i) => (
            <div key={i} className="px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <WarningIcon />
                <div>
                  <span className="text-sm font-medium text-slate-900">{check.name}</span>
                  <p className="text-xs text-slate-500">{check.detail}</p>
                </div>
              </div>
              {check.action && (
                <a href={check.action} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" className="gap-1.5 text-xs">
                    Open <ExternalLink className="w-3 h-3" />
                  </Button>
                </a>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Recommendations */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
          <Search className="w-5 h-5 text-teal-600" />
          GEO Recommendations (AI Search Visibility)
        </h2>
        <div className="space-y-3 text-sm text-slate-600">
          <div className="flex items-start gap-3">
            <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
            <span><strong>Schema.org structured data</strong> — Implemented on all pages (Organisation, Service, FAQPage, BlogPosting, BreadcrumbList)</span>
          </div>
          <div className="flex items-start gap-3">
            <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
            <span><strong>Clear, factual content</strong> — Definition sections, methodology, scope limitations present</span>
          </div>
          <div className="flex items-start gap-3">
            <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
            <span><strong>FAQ content with schema</strong> — 15 FAQs with FAQPage schema (highly cited by AI engines)</span>
          </div>
          <div className="flex items-start gap-3">
            <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
            <span><strong>Authority signals</strong> — "Since 1984", "40+ years", pricing specifics in Service schema</span>
          </div>
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <span><strong>Bing indexing (critical for ChatGPT)</strong> — Submit sitemap to Bing Webmaster Tools</span>
          </div>
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <span><strong>Backlinks & citations</strong> — Get mentioned on IP directories and link from main NBR site</span>
          </div>
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <span><strong>Content freshness</strong> — Publish new blog posts regularly to signal an active, authoritative source</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SEODashboard;
